import { Chip, colors, Grid, IconButton, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import { IDetailsBoleto } from "interfaces/detailsBoleto";

import { siteConfig } from "types/siteConfig";
import { formatDate, formatDateEndOfDay } from "helpers/formatDate";

//Icones
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import iconBoleto from "assets/images/icons/icon-pago-bol.png";
import { StatusPayBoletoCartao } from "interfaces/payment";
import { whatsappShare } from "helpers/whatsapp";

interface Props {
  detailsBoleto: IDetailsBoleto;
}

export default function DetailsBoleto({ detailsBoleto }: Props) {
  // Mapeamento dos status para valores amigáveis
  const statusMap: Record<StatusPayBoletoCartao, string> = {
    new: "Novo",
    waiting: "Aguardando",
    identified: "Identificado",
    approved: "Aprovado",
    paid: "Pago",
    unpaid: "Não Pago",
    refunded: "Reembolsado",
    contested: "Contestação",
    canceled: "Cancelado",
    settled: "Liquidado Manualmente",
    link: "Link",
    expired: "Expirado",
  };

  // Função para converter o status
  function getStatus(status: StatusPayBoletoCartao): string {
    return statusMap[status] || "Status desconhecido";
  }
  function handleWhatsapp() {
    whatsappShare({
      message: `Olá, _${detailsBoleto.data.customer.name.toUpperCase()}_ segue o link do boleto no valor de *${convertToCurrency(
        detailsBoleto.data.total
      )}*. Cobrança referente à Inscrição.\n\n${
        detailsBoleto.data.payment.banking_billet.link
      }\n\n${siteConfig.name}.`,
    });
  }

  /**
   * Converts a numeric value to a currency format string in Brazilian locale.
   *
   * @param value - The value to be converted, can be a string or number.
   * @returns A string representing the value in Brazilian currency format.
   */
  function convertToCurrency(value: string | number): string {
    const valueConvert = typeof value === "string" ? Number(value) : value;
    const formattedValue = (valueConvert / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formattedValue;
  }

  return (
    <>
      <MDBox sx={{ my: 3, p: 2, overflowY: "scroll", height: "70vh" }}>
        {/* Barra de status e ações */}
        <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
          <Chip
            label={getStatus(detailsBoleto.data.status)}
            variant="outlined"
            color={detailsBoleto.data.status === "paid" ? "success" : "warning"}
          />
          <MDBox sx={{ display: "flex", gap: 1, mt: 1 }}>
            <IconButton onClick={handleWhatsapp}>
              <WhatsAppIcon />
            </IconButton>
          </MDBox>
        </MDBox>

        {/* Detalhes */}
        <MDBox sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Valor da cobrança:
            </Typography>
            <Typography variant="h4" color="orange">
              {convertToCurrency(detailsBoleto.data.total)}
            </Typography>
          </MDBox>

          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Vencimento:
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="GrayText">
              {formatDateEndOfDay(
                detailsBoleto.data.payment.banking_billet.expire_at,
                "dd/MM/yyyy"
              )}
            </Typography>
          </MDBox>

          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              N. da Cobrança:
            </Typography>
            <Typography variant="body2" color="GrayText">
              {detailsBoleto.data.charge_id}
            </Typography>
          </MDBox>

          <MDBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Pagador:
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="GrayText">
              {`${detailsBoleto.data.customer.name.toUpperCase()} | ${
                detailsBoleto.data.customer.cpf
              }`}
            </Typography>
          </MDBox>
        </MDBox>

        {/* Informações do boleto */}
        <MDBox
          sx={{
            bgcolor: colors.grey[100],
            p: 2,
            mt: 2,
            borderRadius: 2,
            border: "1px dashed #ccc",
            display: "flex",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          {/* Informações do boleto */}
          <MDBox>
            <Typography variant="body2" fontWeight="bold">
              Descrição
            </Typography>

            {detailsBoleto.data.items.map((item) => (
              <Typography key={item.name} variant="body2">
                {item.name}
              </Typography>
            ))}

            <Typography
              variant="body2"
              sx={{ wordBreak: "break-word", mt: 2, letterSpacing: 2 }}
            >
              {detailsBoleto.data.payment.banking_billet.barcode}
            </Typography>
          </MDBox>

          {/* Image do boleto se estiver pago */}
          {(detailsBoleto.data.status === "settled" ||
            detailsBoleto.data.status === "paid") && (
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                width: 250,
              }}
            >
              <img src={iconBoleto} width={60} alt="image qrcod pix" />
              <Typography variant="body1" fontWeight="bold" color="GrayText">
                Boleto já está pago!
              </Typography>
            </MDBox>
          )}
        </MDBox>

        {/* Histórico do boleto */}
        <MDBox
          sx={{
            bgcolor: colors.grey[100],
            p: 2,
            mt: 2,
            borderRadius: 2,
            border: "1px dashed #ccc",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Histórico da cobraça
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" fontWeight="bold">
                Data/Hora
              </Typography>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography variant="body2" fontWeight="bold">
                Messagem
              </Typography>
            </Grid>
          </Grid>

          {detailsBoleto.data.history.map((history, idx) => (
            <MDBox key={idx}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2">
                    🔸 {formatDate(history.created_at, "dd/MM/yyyy HH:mm:ss")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="body2">{history.message}</Typography>
                </Grid>
              </Grid>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        sx={{ mt: 2, mr: 3, textAlign: "right", color: "GrayText" }}
      >
        Criado em:{" "}
        {formatDate(detailsBoleto.data.created_at, "dd/MM/yyyy HH:mm:ss")}
      </Typography>
    </>
  );
}
